import { useState, useEffect } from 'react';
import { X, Trash2, Pencil } from 'lucide-react';

/**
 * AgentsPopup component displays a list of agents from localStorage.
 * Each agent includes:
 *   - name
 *   - schedule
 *   - questions array
 *   - notification channels
 */
export const AgentsPopup = ({ isOpen, onClose }) => {
  const [agents, setAgents] = useState([]);
  const [editingField, setEditingField] = useState(null); // Format: "agentId-fieldName"

  const formatSchedule = (schedule) => {
    if (!schedule) return 'No schedule set';
    return `${schedule.startDate} at ${schedule.startTime}, ${schedule.frequency}`;
  };

  const getQuestions = (agent) => {
    if (!agent.chat?.messages) return [];
    
    // If messages is an array of objects (new format), use it
    if (typeof agent.chat.messages[0] === 'object' && agent.chat.messages[0].question) {
      return agent.chat.messages.map(msg => ({
        question: msg.question,
        answer: msg.answer
      }));
    }
    
    // Fall back to old format (array of strings)
    return agent.chat.messages.map(msg => ({
      question: msg,
      answer: null
    }));
  };

  const loadAgents = () => {
    const storedData = localStorage.getItem('agents');
    if (storedData) {
      try {
        setAgents(JSON.parse(storedData));
      } catch (error) {
        console.error('Failed to parse agents data:', error);
      }
    }
  };

  // Load agents when popup opens
  useEffect(() => {
    if (isOpen) {
      loadAgents();
    }
  }, [isOpen]);

  const handleDeleteAgent = (agentId) => {
    const updatedAgents = agents.filter(agent => agent.id !== agentId);
    localStorage.setItem('agents', JSON.stringify(updatedAgents));
    setAgents(updatedAgents);
  };

  const handleSave = (agentId, field, value) => {
    const updatedAgents = agents.map(agent => {
      if (agent.id !== agentId) return agent;

      const updatedAgent = { ...agent };
      
      switch (field) {
        case 'name':
          updatedAgent.name = value;
          break;
        case 'schedule':
          updatedAgent.schedule = {
            ...agent.schedule,
            ...value
          };
          break;
        case 'notifications':
          updatedAgent.notifications = value;
          break;
      }

      return updatedAgent;
    });

    setAgents(updatedAgents);
    localStorage.setItem('agents', JSON.stringify(updatedAgents));
    setEditingField(null);
  };

  const renderEditableField = (agent, field) => {
    const isEditing = editingField === `${agent.id}-${field}`;
    
    switch (field) {
      case 'name':
        return isEditing ? (
          <input
            type="text"
            defaultValue={agent.name}
            onBlur={(e) => handleSave(agent.id, 'name', e.target.value)}
            className="border rounded px-2 py-1 text-sm w-full"
            autoFocus
          />
        ) : (
          <span 
            onClick={() => setEditingField(`${agent.id}-${field}`)}
            className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
          >
            {agent.name}
          </span>
        );

      case 'schedule':
        return isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="date"
              defaultValue={agent.schedule?.startDate}
              onBlur={(e) => {
                const newSchedule = {
                  ...agent.schedule,
                  startDate: e.target.value
                };
                handleSave(agent.id, 'schedule', newSchedule);
              }}
              className="border rounded px-2 py-1 text-sm"
              autoFocus
            />
            <input
              type="time"
              defaultValue={agent.schedule?.startTime}
              onBlur={(e) => {
                const newSchedule = {
                  ...agent.schedule,
                  startTime: e.target.value
                };
                handleSave(agent.id, 'schedule', newSchedule);
              }}
              className="border rounded px-2 py-1 text-sm"
            />
            <select
              defaultValue={agent.schedule?.frequency}
              onBlur={(e) => {
                const newSchedule = {
                  ...agent.schedule,
                  frequency: e.target.value
                };
                handleSave(agent.id, 'schedule', newSchedule);
              }}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="once">Once</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        ) : (
          <span 
            onClick={() => setEditingField(`${agent.id}-${field}`)}
            className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
          >
            {formatSchedule(agent.schedule)}
          </span>
        );

      case 'notifications':
        return isEditing ? (
          <div className="space-y-2">
            {Object.entries(agent.notifications).map(([channel, config]) => (
              <div key={channel} className="flex items-center gap-2">
                <span className="w-20">{channel}:</span>
                <input
                  type="text"
                  defaultValue={Array.isArray(config) ? config.join(', ') : config.recipients}
                  onBlur={(e) => {
                    const newNotifications = {
                      ...agent.notifications,
                      [channel]: { recipients: e.target.value }
                    };
                    handleSave(agent.id, 'notifications', newNotifications);
                  }}
                  className="border rounded px-2 py-1 text-sm flex-1"
                  placeholder={`Enter ${channel} recipients`}
                />
              </div>
            ))}
          </div>
        ) : (
          <span 
            onClick={() => setEditingField(`${agent.id}-${field}`)}
            className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
          >
            {Object.entries(agent.notifications || {})
              .map(([channel, config]) => {
                const recipients = Array.isArray(config) 
                  ? config.join(', ')
                  : config.recipients;
                return `${channel}: ${recipients}`;
              })
              .join('; ')}
          </span>
        );
    }
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Dialog */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-medium">Agents</h2>
                <button
                  onClick={onClose}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                  aria-label="Close Agents Popup"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-6">
                <div className="grid grid-cols-1 gap-4">
                  {agents.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No agents found. Create agents in the replay dialog or elsewhere.
                    </p>
                  ) : (
                    agents.map((agent, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4 shadow-sm text-sm relative group"
                      >
                        <button
                          onClick={() => handleDeleteAgent(agent.id)}
                          className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Delete Agent"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="mb-2">
                              <span className="font-semibold">Name:</span>{' '}
                              {renderEditableField(agent, 'name')}
                            </div>
                            <div className="mb-2">
                              <span className="font-semibold">Schedule:</span>{' '}
                              {renderEditableField(agent, 'schedule')}
                            </div>
                            <div className="mb-2">
                              <span className="font-semibold">Notification Channels:</span>{' '}
                              {renderEditableField(agent, 'notifications')}
                            </div>
                          </div>
                          <div>
                            <span className="font-semibold">Questions:</span>
                            <ul className="space-y-2 mt-1">
                              {getQuestions(agent).length > 0 ? (
                                getQuestions(agent).map((item, qIdx) => (
                                  <li key={qIdx} className="space-y-1">
                                    <div className="text-gray-700">{item.question}</div>
                                    {item.answer && (
                                      <div className="text-gray-600 text-xs pl-4 italic">
                                        Answer: {item.answer}
                                      </div>
                                    )}
                                  </li>
                                ))
                              ) : (
                                <li className="text-gray-500">No questions defined</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}; 